import { trackToolStep, trackToolResult, trackToolLead, trackToolCta } from "@/lib/formTelemetry";
import { trackEvent } from "@/lib/analytics";
import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, ChevronRight, Lock } from "lucide-react";
import { useNavigate } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuthState } from "@/hooks/useAuthState";
import { useHasActivePanel } from "@/hooks/useHasActivePanel";
import { usePlanGate } from "@/hooks/usePlanUi";
import { RETAIL_SECTORS, SectorIcon } from "@/components/SectorOnboardingModal";
import { getModuleLabel } from "@/lib/module-labels";
import { setPostAuthIntent } from "@/lib/post-auth-intent";
import { trackPublicEvent } from "@/lib/publicActivity";
import { startProCheckout } from "@/lib/proCheckout";
import { supabase } from "@/integrations/supabase/client";
import { EXPRESS_MODULES, LEVEL_META } from "@/lib/expressDiagnostic";
import { EXPRESS_UNLOCK_PATH } from "@/components/express/ExpressLeakDetector";
import { useServerFn } from "@tanstack/react-start";
import { getModuleNarrative } from "@/lib/fullDiagnosticNarrative.functions";
import {
  fullNarrativeFallback,
  fullDiagnosisFallback,
  moduleAnswerPairs,
  FULL_QUESTIONS,
  TOTAL_QUESTIONS,
  BLOCK_LABEL,
  PROFILE_STEPS,
  STAGE_OPTIONS,
  LEGAL_OPTIONS,
  TEAM_OPTIONS,
  EMPTY_PROFILE,
  INFO_LABEL,
  helperFor,
  computeFullResult,
  fullHeadline,
  loadFullState,
  saveFullState,
  persistFullDiagnostic,
  claimFullDiagnostic,
  syncExpressFromFull,
  type FullAnswers,
  type FullModuleKey,
  type FullResult,
  type FullState,
  type GuestProfile,
  type ProfileStep,
} from "@/lib/fullDiagnostic";

type Phase = "profile" | "quiz" | "result" | "detail";

const FALLBACK_EXTRA_MODULES = ["ia_automatizacion", "blindaje_legal"];

interface Props {
  id?: string;
  onOpenAuth: (intentPath: string) => void;
  onPhaseChange?: (phase: Phase) => void;
}

/**
 * Detector de Fugas Completo, embebido en la página (sin pop-up, sin cambiar
 * de URL). Perfil de invitado → 32 preguntas → resultado con semáforo.
 */
const FullLeakDetectorCard = ({ id, onOpenAuth, onPhaseChange }: Props) => {
  const { lang, t } = useLanguage();
  const L: "es" | "en" = lang === "en" ? "en" : "es";
  const es = L === "es";

  const navigate = useNavigate();
  const { isAuthenticated, isReady, user } = useAuthState();
  const { hasPanel, isAdmin } = useHasActivePanel();
  const isClubPro = usePlanGate("tool.detector_completo", hasPanel || isAdmin).allowed;

  const [phase, setPhase] = useState<Phase>("profile");
  const [profileStep, setProfileStep] = useState(0);
  const [profile, setProfile] = useState<GuestProfile>(EMPTY_PROFILE);
  const [skipProfile, setSkipProfile] = useState(false);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState<FullAnswers>({});
  const [result, setResult] = useState<FullResult | null>(null);
  const [detail, setDetail] = useState<FullModuleKey | null>(null);
  const [extraModules, setExtraModules] = useState<string[]>(FALLBACK_EXTRA_MODULES);
  const [restored, setRestored] = useState(false);
  const [narratives, setNarratives] = useState<Partial<Record<FullModuleKey, string>>>({});
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const fetchNarrative = useServerFn(getModuleNarrative);
  const topRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => { onPhaseChange?.(phase); }, [phase, onPhaseChange]);

  // Estado previo del navegador.
  useEffect(() => {
    const saved = loadFullState();
    if (saved) {
      setProfile({ ...EMPTY_PROFILE, ...saved.profile });
      setAnswers(saved.answers);
      if (saved.result) { setResult(saved.result); setPhase("result"); }
    }
    setRestored(true);
    void trackPublicEvent({ eventType: "tool_view", toolKey: "termometro", lang: L, metadata: { full: true } }, { once: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Usuario con perfil: no repetimos el paso 0 y vinculamos el diagnóstico.
  useEffect(() => {
    if (!isReady || !isAuthenticated || !user) return;
    void claimFullDiagnostic();
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("retail_sector, company_name, business_stage, legal_form, city, team_size")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled || !data?.retail_sector) return;
      setProfile((p) => ({
        stage: (p.stage ?? (data.business_stage as GuestProfile["stage"])) ?? "abierto",
        sector: p.sector ?? data.retail_sector,
        legal_form: p.legal_form ?? data.legal_form ?? null,
        city: p.city || data.city || "",
        team_size: p.team_size ?? data.team_size ?? null,
        business_name: p.business_name || data.company_name || "",
      }));
      setSkipProfile(true);
      setPhase((ph) => (ph === "profile" ? "quiz" : ph));
    })();
    return () => { cancelled = true; };
  }, [isReady, isAuthenticated, user]);

  // Extras premium según back office (cualquier addon/premium aparece bloqueado).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from("module_content")
          .select("module_key, access_type, sort_order")
          .eq("lang", "es")
          .in("access_type", ["addon", "premium"])
          .order("sort_order", { ascending: true });
        if (cancelled || !data) return;
        const keys: string[] = [];
        for (const row of data as { module_key: string }[]) {
          if (!row.module_key || (EXPRESS_MODULES as readonly string[]).includes(row.module_key)) continue;
          if (!keys.includes(row.module_key)) keys.push(row.module_key);
        }
        if (keys.length > 0) setExtraModules(keys);
      } catch { /* fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // El paso que se muestra al usuario es el bloque (área), no la pregunta.
  const blockOrder = useMemo(() => {
    const seen: string[] = [];
    for (const q of FULL_QUESTIONS) if (!seen.includes(q.block)) seen.push(q.block);
    return seen;
  }, []);
  const totalSteps = blockOrder.length;
  const currentStep =
    phase === "profile" ? 1
    : phase === "quiz" ? Math.max(1, blockOrder.indexOf(FULL_QUESTIONS[idx]?.block ?? blockOrder[0]!) + 1)
    : totalSteps;
  const barWidth = Math.round((Math.min(phase === "quiz" ? idx + 1 : phase === "profile" ? 0.5 : TOTAL_QUESTIONS, TOTAL_QUESTIONS) / TOTAL_QUESTIONS) * 100);

  const question = FULL_QUESTIONS[idx];
  const sectorLabel = useMemo(() => {
    const f = RETAIL_SECTORS.find((s) => s.key === profile.sector);
    return f ? (es ? f.label : f.labelEn) : "";
  }, [profile.sector, es]);

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const persistDraft = (p: GuestProfile, a: FullAnswers) => {
    saveFullState({ profile: p, answers: a, result: null, completedAt: null, claimed: false });
  };

  const setProfileField = <K extends keyof GuestProfile>(key: K, value: GuestProfile[K]) => {
    const next = { ...profile, [key]: value };
    setProfile(next);
    if (key === "sector" && typeof value === "string") {
      try { window.localStorage.setItem("rl-guest-sector", value); } catch { /* ignore */ }
    }
    return next;
  };

  const nextProfileStep = (next: GuestProfile) => {
    trackToolStep("detector_fugas_completo", profileStep + 1, `perfil_${profileStep + 1}`);
    if (profileStep + 1 < PROFILE_STEPS.length) { setProfileStep(profileStep + 1); return; }
    persistDraft(next, answers);
    void persistFullDiagnostic({ profile: next, answers, result: null, completedAt: null, claimed: false }, L);
    setPhase("quiz");
    setIdx(0);
    scrollTop();
  };

  const answer = (optionId: string) => {
    if (!question) return;
    const next = { ...answers, [question.id]: optionId };
    setAnswers(next);
    trackToolStep("detector_fugas_completo", 10 + idx + 1, `pregunta_${idx + 1}`);
    if (idx + 1 < TOTAL_QUESTIONS) { setIdx(idx + 1); return; }
    const r = computeFullResult(next, L);
    const state: FullState = { profile, answers: next, result: r, completedAt: new Date().toISOString(), claimed: false };
    setResult(r);
    saveFullState(state);
    syncExpressFromFull(state, isAuthenticated);
    void persistFullDiagnostic(state, L, { completed: true });
    if (isAuthenticated) void claimFullDiagnostic();
    void trackPublicEvent({ eventType: "tool_view", toolKey: "termometro", sector: profile.sector, lang: L, metadata: { full_completed: true, scores: r.scores } });
    trackEvent("tool_use", { tool_name: "detector_fugas", tool_variant: "completo", retail_sector: profile.sector });
    trackToolResult("detector_fugas_completo");
    setPhase("result");
    scrollTop();
  };

  const back = () => {
    if (phase === "quiz") {
      if (idx > 0) setIdx(idx - 1);
      else if (!skipProfile) { setPhase("profile"); setProfileStep(PROFILE_STEPS.length - 1); }
      return;
    }
    if (phase === "profile" && profileStep > 0) setProfileStep(profileStep - 1);
  };

  const restart = () => {
    setAnswers({}); setResult(null); setIdx(0); setDetail(null);
    saveFullState({ profile, answers: {}, result: null, completedAt: null, claimed: false });
    if (skipProfile) setPhase("quiz"); else { setProfileStep(0); setPhase("profile"); }
    scrollTop();
  };

  const openDetail = (m: FullModuleKey) => {
    setDetail(m); setPhase("detail");
    const st = loadFullState();
    if (st) void persistFullDiagnostic(st, L, { openedModule: m });

    // Párrafo de asesoría escrito con IA a partir de SUS respuestas de ese módulo.
    if (narratives[m] || !result) return;
    const pairs = moduleAnswerPairs(m, answers, L);
    if (pairs.length === 0) return;
    setNarrativeLoading(true);
    void fetchNarrative({
      data: { lang: L, sectorLabel, moduleLabel: getModuleLabel(m, L), level: result.modules[m].level, answers: pairs },
    })
      .then((res) => {
        if (res?.status === "ok" && res.paragraph) setNarratives((prev) => ({ ...prev, [m]: res.paragraph }));
      })
      .catch(() => undefined)
      .finally(() => setNarrativeLoading(false));
  };

  const unlockFirstSteps = () => {
    void trackPublicEvent({ eventType: "cta_register", toolKey: "termometro", sector: profile.sector, lang: L, metadata: { full_unlock: true } });
    trackToolLead("detector_fugas_completo", "registro", "completo");
    trackToolCta("detector_fugas_completo", "registro", undefined, "completo");
    if (isAuthenticated) { navigate(EXPRESS_UNLOCK_PATH); return; }
    setPostAuthIntent({ kind: "path", path: EXPRESS_UNLOCK_PATH });
    onOpenAuth(EXPRESS_UNLOCK_PATH);
  };

  const joinClubPro = async () => {
    void trackPublicEvent({ eventType: "cta_pro", toolKey: "termometro", sector: profile.sector, lang: L, metadata: { full_club_pro: true } });
    trackToolCta("detector_fugas_completo", "pago", "club_pro", "completo"); // startProCheckout abre el Club Pro
    const res = await startProCheckout({ isAuthenticated, lang: L });
    if (res === "needs-auth") {
      setPostAuthIntent({ kind: "path", path: "/dashboard" });
      onOpenAuth("/dashboard");
    }
  };

  const optionBtn = "flex min-h-[56px] w-full items-center justify-between gap-3 rounded-xl border border-border bg-background px-4 py-3 text-left transition-colors hover:border-primary hover:bg-secondary";

  const renderOptions = (opts: { id: string; es: string; en: string }[], onPick: (id: string) => void, selected?: string | null) => (
    <div className="mt-4 grid gap-3">
      {opts.map((op) => (
        <button
          key={op.id}
          type="button"
          onClick={() => onPick(op.id)}
          aria-pressed={selected === op.id}
          className={cn(optionBtn, selected === op.id && "border-primary bg-primary/10")}
        >
          <span className="min-w-0 break-words text-sm font-medium sm:text-base">{es ? op.es : op.en}</span>
          <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
        </button>
      ))}
    </div>
  );

  const stepLabel =
    phase === "result" || phase === "detail"
      ? (es ? "Resultado" : "Result")
      : (es ? `Paso ${currentStep} de ${totalSteps}` : `Step ${currentStep} of ${totalSteps}`);

  const pStep: ProfileStep | undefined = PROFILE_STEPS[profileStep];

  return (
    <div
      id={id}
      ref={topRef}
      className="mt-4 flex min-h-[340px] scroll-mt-20 flex-col rounded-2xl border-[1.5px] border-primary bg-card p-4 text-left shadow-[0_0_0_4px_color-mix(in_oklab,var(--primary)_18%,transparent)] sm:p-5"
    >
      {/* Cabecera de paso */}
      <div>
        <p className="text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{stepLabel}</p>
        <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${barWidth}%` }} />
        </div>
      </div>

      {!restored && <div className="flex-1" />}

      {/* PASO 0 · perfil */}
      {restored && phase === "profile" && pStep && (
        <div className="flex flex-1 flex-col">
          <p className="mt-5 text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{es ? "Tu negocio" : "Your business"}</p>
          {pStep === "stage" && (<>
            <h2 className="font-display mt-2 text-center text-lg font-bold tracking-tight sm:text-xl">{es ? "¿En qué punto está tu negocio hoy?" : "Where is your business today?"}</h2>
            {renderOptions([...STAGE_OPTIONS], (v) => nextProfileStep(setProfileField("stage", v as GuestProfile["stage"])), profile.stage)}
          </>)}
          {pStep === "sector" && (<>
            <h2 className="font-display mt-2 text-center text-lg font-bold tracking-tight sm:text-xl">{es ? "¿Qué tipo de negocio tienes?" : "What kind of business do you run?"}</h2>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {RETAIL_SECTORS.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => nextProfileStep(setProfileField("sector", s.key))}
                  className={cn("flex min-h-11 items-center gap-2 rounded-xl border border-border bg-background px-3 py-2 text-left text-sm transition-colors hover:border-primary hover:bg-secondary", profile.sector === s.key && "border-primary bg-primary/10")}
                >
                  <SectorIcon sector={s.key} className="h-4 w-4 shrink-0 text-foreground" />
                  <span className="truncate">{es ? s.label : s.labelEn}</span>
                </button>
              ))}
            </div>
          </>)}
          {pStep === "legal" && (<>
            <h2 className="font-display mt-2 text-center text-lg font-bold tracking-tight sm:text-xl">{es ? "¿Cómo estás constituido?" : "What's your legal form?"}</h2>
            {renderOptions([...LEGAL_OPTIONS], (v) => nextProfileStep(setProfileField("legal_form", v)), profile.legal_form)}
          </>)}
          {pStep === "city" && (
            <form className="flex flex-1 flex-col" onSubmit={(e) => { e.preventDefault(); nextProfileStep(profile); }}>
              <h2 className="font-display mt-2 text-center text-lg font-bold tracking-tight sm:text-xl">{es ? "¿En qué ciudad o provincia está el local?" : "Which city or province is the shop in?"}</h2>
              <input
                type="text"
                value={profile.city}
                onChange={(e) => setProfileField("city", e.target.value)}
                maxLength={120}
                placeholder={es ? "Ej. Valencia" : "E.g. Valencia"}
                className="mt-4 min-h-[52px] w-full rounded-xl border border-border bg-background px-4 text-base outline-none focus:border-primary"
                aria-label={es ? "Ciudad o provincia" : "City or province"}
              />
              <Button type="submit" disabled={!profile.city.trim()} className="mt-3 h-12 w-full bg-primary text-base font-semibold text-primary-foreground hover:bg-primary/90">
                {es ? "Continuar" : "Continue"} <ArrowRight className="h-4 w-4" />
              </Button>
            </form>
          )}
          {pStep === "team" && (<>
            <h2 className="font-display mt-2 text-center text-lg font-bold tracking-tight sm:text-xl">{es ? "¿Cuánta gente sois?" : "How many people are you?"}</h2>
            {renderOptions([...TEAM_OPTIONS], (v) => nextProfileStep(setProfileField("team_size", v)), profile.team_size)}
          </>)}
          {pStep === "name" && (
            <form className="flex flex-1 flex-col" onSubmit={(e) => { e.preventDefault(); nextProfileStep(profile); }}>
              <h2 className="font-display mt-2 text-center text-lg font-bold tracking-tight sm:text-xl">{es ? "¿Cómo se llama tu negocio?" : "What's your business called?"}</h2>
              <p className="mt-1 text-center text-xs text-muted-foreground">{es ? "Opcional" : "Optional"}</p>
              <input
                type="text"
                value={profile.business_name}
                onChange={(e) => setProfileField("business_name", e.target.value)}
                maxLength={120}
                placeholder={es ? "Ej. Café Central" : "E.g. Café Central"}
                className="mt-4 min-h-[52px] w-full rounded-xl border border-border bg-background px-4 text-base outline-none focus:border-primary"
                aria-label={es ? "Nombre del negocio" : "Business name"}
              />
              <Button type="submit" className="mt-3 h-12 w-full bg-primary text-base font-semibold text-primary-foreground hover:bg-primary/90">
                {profile.business_name.trim() ? (es ? "Empezar el diagnóstico" : "Start the diagnosis") : (es ? "Saltar y empezar" : "Skip and start")} <ArrowRight className="h-4 w-4" />
              </Button>
            </form>
          )}
          <div className="mt-4 flex items-center justify-between">
            {profileStep > 0 ? (
              <button type="button" onClick={back} className="inline-flex min-h-11 items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-3.5 w-3.5" /> {es ? "Atrás" : "Back"}
              </button>
            ) : (
              <span />
            )}
            <span className="text-xs text-muted-foreground">{es ? "Toca una opción para seguir" : "Tap an option to continue"}</span>
          </div>
        </div>
      )}

      {/* PREGUNTAS */}
      {restored && phase === "quiz" && question && (
        <div className="flex flex-1 flex-col" key={question.id}>
          <p className="mt-5 text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {BLOCK_LABEL[question.block][L]}
          </p>
          <h2 className="font-display mt-2 text-center text-lg font-bold tracking-tight text-balance sm:text-xl">{es ? question.es : question.en}</h2>
          {helperFor(question, profile.sector, L) && (
            <p className="mt-2 rounded-lg bg-muted px-3 py-2 text-center text-xs leading-snug text-muted-foreground">{helperFor(question, profile.sector, L)}</p>
          )}
          {renderOptions(question.options, answer, answers[question.id])}
          <div className="mt-4 flex items-center justify-between">
            <button type="button" onClick={back} className="inline-flex min-h-11 items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-3.5 w-3.5" /> {es ? "Atrás" : "Back"}
            </button>
            {idx > 0 && (
              <a href="/" className="inline-flex min-h-11 items-center text-xs text-muted-foreground hover:text-foreground">{es ? "Inicio" : "Home"}</a>
            )}
          </div>
        </div>
      )}

      {/* RESULTADO */}
      {restored && phase === "result" && result && (
        <div className="flex flex-1 flex-col space-y-4 pt-5">
          <div className="text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
              {es ? `Tu radiografía${sectorLabel ? ` · ${sectorLabel}` : ""}` : `Your X-ray${sectorLabel ? ` · ${sectorLabel}` : ""}`}
            </p>
            <h2 className="font-display mt-2 text-xl font-extrabold tracking-tight text-balance sm:text-2xl">{fullHeadline(result, L)}</h2>
          </div>

          {!isClubPro && (
            <Button onClick={joinClubPro} className="h-12 w-full bg-primary text-base font-semibold text-primary-foreground hover:bg-primary/90">
              {t("fullDiagnostic", "joinClubProCta")}
            </Button>
          )}

          <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border">
            {EXPRESS_MODULES.map((m) => {
              const r = result.modules[m];
              const meta = LEVEL_META[r.level];
              return (
                <li key={m}>
                  <button type="button" onClick={() => openDetail(m)} className="flex min-h-12 w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-muted/50">
                    <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", meta.dot)} aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold">{getModuleLabel(m, L)}</span>
                      <span className="block truncate text-xs leading-snug text-muted-foreground">{r.orientation}</span>
                    </span>
                    <span className={cn("shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-bold", meta.badge)}>{es ? meta.es : meta.en}</span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                </li>
              );
            })}
          </ul>

          {extraModules.length > 0 && (
            <div>
              <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{es ? "Módulos extra" : "Extra modules"}</p>
              <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border">
                {extraModules.map((key) => {
                  const rec = (key === "ia_automatizacion" && result.extras.ia.recommended) || (key === "blindaje_legal" && result.extras.legal.recommended);
                  return (
                    <li key={key} className="flex min-h-12 items-center gap-2 px-3 py-2">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#C4C4C4]" aria-hidden="true" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold">{getModuleLabel(key, L)}</span>
                        <span className="block text-xs leading-snug text-muted-foreground">
                          <a href={es ? "/tarifas" : "/en/pricing"} className="inline-flex min-h-11 items-center font-semibold text-foreground underline underline-offset-4">{t("leakExtras", "includedInAdvanced")}</a>{rec ? (es ? " · Recomendado" : " · Recommended") : ""}
                        </span>
                      </span>
                      <Lock className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={2} aria-label={es ? "Bloqueado" : "Locked"} />
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {!isAuthenticated && (
            <div className="space-y-2 pt-1">
              <Button variant="secondary" onClick={unlockFirstSteps} className="h-12 w-full text-base font-semibold text-foreground hover:bg-secondary/80">
                {t("fullDiagnostic", "unlockFirstSteps")}
              </Button>
              <p className="text-center text-xs text-muted-foreground">{t("fullDiagnostic", "unlockFirstStepsMicro")}</p>
            </div>
          )}

          {isAuthenticated && !isClubPro && (
            <Button variant="secondary" onClick={unlockFirstSteps} className="h-12 w-full text-base font-semibold text-foreground hover:bg-secondary/80">
              {t("fullDiagnostic", "seeFirstSteps")}
            </Button>
          )}

          <button type="button" onClick={restart} className="mx-auto block min-h-11 text-xs text-muted-foreground underline-offset-4 hover:underline">
            {t("fullDiagnostic", "retake")}
          </button>
        </div>
      )}


      {/* DETALLE DE MÓDULO */}
      {restored && phase === "detail" && result && detail && (
        <div className="flex flex-1 flex-col space-y-4 pt-5">
          <button type="button" onClick={() => setPhase("result")} className="inline-flex min-h-11 items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> {es ? "Volver a la radiografía" : "Back to X-ray"}
          </button>
          <div className="flex items-center gap-3">
            <span className={cn("h-3 w-3 shrink-0 rounded-full", LEVEL_META[result.modules[detail].level].dot)} aria-hidden="true" />
            <h2 className="font-display text-lg font-bold">{getModuleLabel(detail, L)}</h2>
            <span className={cn("ml-auto shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-bold", LEVEL_META[result.modules[detail].level].badge)}>
              {es ? LEVEL_META[result.modules[detail].level].es : LEVEL_META[result.modules[detail].level].en}
            </span>
          </div>
          <p aria-busy={narrativeLoading} className="text-sm leading-relaxed text-muted-foreground">
            {narratives[detail] ?? fullDiagnosisFallback(detail, result.modules[detail].level, L)}
          </p>

          <div className="relative overflow-hidden rounded-2xl border border-border bg-background p-4">
            <div className={cn("space-y-3", !isClubPro && "select-none blur-[5px] pointer-events-none")} aria-hidden={!isClubPro}>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{es ? "Tu plan de la semana" : "Your plan for the week"}</p>
                <p className="mt-0.5 text-sm">{result.modules[detail].action}</p>
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{es ? "Dónde trabajarlo" : "Where to work on it"}</p>
                <p className="mt-0.5 text-sm">
                  {es
                    ? `Módulo ${getModuleLabel(detail, L)} del panel · ${INFO_LABEL[result.modules[detail].info][L]}`
                    : `${getModuleLabel(detail, L)} module in the panel · ${INFO_LABEL[result.modules[detail].info][L]}`}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{es ? "Cómo mejorarlo" : "How to improve it"}</p>
                <p className="mt-0.5 text-sm">{fullNarrativeFallback(detail, result.modules[detail].level, L)}</p>
              </div>
            </div>
            {!isClubPro && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-card/70 px-4 text-center">
                <Lock className="h-5 w-5 text-foreground" strokeWidth={1.5} />
                <p className="max-w-[36ch] text-sm font-semibold">
                  {es
                    ? "Cómo mejorar este punto — y los que ahora no estás viendo."
                    : "How to improve this point — and the ones you can't see yet."}
                </p>
                <Button onClick={joinClubPro} className="h-11 bg-primary text-sm font-semibold text-primary-foreground hover:bg-primary/90">
                  {es ? "Unirme al Club Pro · 69 €/mes" : "Join Club Pro · 69 €/month"}
                </Button>
              </div>
            )}
          </div>

          {isClubPro && (
            <Button onClick={() => navigate(`/dashboard?tab=roadmap&module=${detail}`)} className="h-12 w-full bg-primary text-base font-semibold text-primary-foreground hover:bg-primary/90">
              {es ? "Abrir módulo en el panel" : "Open module in the panel"} <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

    </div>
  );
};

export default FullLeakDetectorCard;
